/**
 * Admin platform-default cancellation policy page — EDITABLE since migration
 * 0051 (`platform_settings` singleton; the F17-era read-only/501 posture is
 * retired).
 *
 * Composes the shared `<CancellationPolicyEditor>` (the same control the F42
 * venue tab + portal owner editor use) + `<CancelTierTrack>` live preview.
 * Saves through `PUT /admin/cancellation-policy/default` with If-Match
 * optimistic concurrency: 409 STALE_PLATFORM_DEFAULT → conflict toast +
 * refetch. Gated by `bookings:edit_cancellation_policy` (route-level); the
 * Save button also disables when the GET failed (no If-Match token).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  CancellationPolicyEditor,
  CancelTierTrack,
  Card,
  Skeleton,
  useToast,
} from '@bravemove/ui-web';
import {
  CancellationPolicySchema,
  type CancellationPolicyTier,
  type Money,
} from '@bravemove/types';
import {
  getPlatformDefault,
  putPlatformDefault,
  type PlatformDefaultRead,
} from '../api/platformCancellationPolicy';
import { PageHeader } from '../components/PageHeader';
import { translateApiError } from '../auth/errors.js';

/**
 * Sample basePrice for the live `CancelTierTrack` preview — pinned at $50.00
 * (5000 USD minor units). Matches the portal F12 cancellation-policy tab
 * convention so the previewed refund-tier visual reads the same on both
 * surfaces.
 */
const SAMPLE_BASE_PRICE: Money = { amount: 5000, currency: 'USD' };
const SAMPLE_HOURS_TO_START = 24;

function errorCode(err: unknown): string | undefined {
  return err && typeof err === 'object' && 'body' in err
    ? (err as { body?: { error?: { code?: string } } }).body?.error?.code
    : undefined;
}

export function AdminCancellationPolicyPage() {
  const { t } = useI18n();
  const toast = useToast();
  const [loaded, setLoaded] = useState<PlatformDefaultRead | null>(null);
  const [tiers, setTiers] = useState<ReadonlyArray<CancellationPolicyTier>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const read = await getPlatformDefault();
      setLoaded(read);
      setTiers(read.policy.tiers);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard load-on-mount
    void reload();
  }, [reload]);

  const valid = useMemo(() => CancellationPolicySchema.safeParse({ tiers }).success, [tiers]);
  const dirty = useMemo(
    () => loaded !== null && JSON.stringify(tiers) !== JSON.stringify(loaded.policy.tiers),
    [tiers, loaded],
  );

  const save = async () => {
    if (!loaded?.updatedAt || !valid) return;
    setSaving(true);
    try {
      const next = await putPlatformDefault({ tiers: [...tiers] }, loaded.updatedAt);
      setLoaded({ policy: next.policy, updatedAt: next.updatedAt, source: 'stored' });
      setTiers(next.policy.tiers);
      toast.push({ tone: 'success', title: t('cancellation:editor.save_success') });
    } catch (err) {
      if (errorCode(err) === 'STALE_PLATFORM_DEFAULT') {
        toast.push({ tone: 'danger', title: t('cancellation:editor.save_conflict') });
        await reload();
      } else {
        toast.push({ tone: 'danger', title: translateApiError(err, t) });
      }
    } finally {
      setSaving(false);
    }
  };

  const previewPolicy = valid ? { tiers: [...tiers] } : (loaded?.policy ?? null);

  return (
    <div className="max-w-3xl flex flex-col gap-4" data-testid="admin-cancellation-policy-page">
      <PageHeader title={t('cancellation:admin.platform_default_heading')} />

      <Card>
        {loading || !loaded ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="flex flex-col gap-4">
            <div data-testid="admin-platform-policy-source">
              <Banner tone="info">
                {loaded.source === 'stored'
                  ? t('cancellation:admin.platform_default_source_stored', {
                      defaultValue:
                        'Custom platform default — applies to every venue without its own policy.',
                    })
                  : t('cancellation:admin.platform_default_source_constant', {
                      defaultValue:
                        'Built-in platform default — edit and save to set a custom platform-wide default.',
                    })}
              </Banner>
            </div>

            <CancellationPolicyEditor tiers={tiers} onChange={setTiers} />

            <div
              className="border-t border-hairline pt-4"
              data-testid="admin-platform-policy-preview"
            >
              <h2 className="text-h2 font-bold text-ink mb-2">
                {t('cancellation:policy.preview_heading')}
              </h2>
              {previewPolicy ? (
                <CancelTierTrack
                  policy={previewPolicy}
                  hoursToStart={SAMPLE_HOURS_TO_START}
                  basePrice={SAMPLE_BASE_PRICE}
                />
              ) : null}
            </div>

            <div className="border-t border-hairline pt-3">
              <Button
                variant="primary"
                size="sm"
                disabled={!dirty || !valid || saving || loaded.updatedAt === null}
                onClick={() => void save()}
                data-testid="admin-platform-policy-save"
              >
                {t('cancellation:editor.save')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default AdminCancellationPolicyPage;
